import { translate as t } from "./locale";
import { useState, type FormEvent } from "react";

import type { AuthenticatedSession, Session } from "./session";
import { registerTelemetrySensitiveValue, trackTelemetryAction, trackTelemetryError } from "./telemetry";

export const formatStudentCode = (input: string): string => {
    const letters = input.toLowerCase().replace(/[^a-z]/gu, "").slice(0, 6);
    return letters.length > 3 ? `${letters.slice(0, 3)}-${letters.slice(3)}` : letters;
};

export const StudentLoginForm = ({
    codeLabel = "Kod ucznia lub trenera",
    disabled,
    formLabel = "Logowanie do konta",
    onLogin,
}: {
    codeLabel?: string;
    disabled: boolean;
    formLabel?: string;
    onLogin: (session: AuthenticatedSession) => void;
}) => {
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!/^[a-z]{3}-[a-z]{3}$/u.test(code)) return;
        setError("");
        setSubmitting(true);
        try {
            const response = await fetch("/api/auth/session", {
                method: "POST",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ code }),
            });
            if (!response.ok) {
                trackTelemetryAction("login", { result: "rejected" });
                setError(t("Nieprawidłowy kod"));
                return;
            }
            const body = (await response.json()) as Session;
            if (!body.authenticated) {
                trackTelemetryAction("login", { result: "rejected" });
                setError(t("Nieprawidłowy kod"));
                return;
            }
            registerTelemetrySensitiveValue(body.user.name);
            trackTelemetryAction("login", {
                accountType: body.user.accountType,
                result: "success",
            });
            onLogin(body);
        } catch (caught) {
            trackTelemetryAction("login", { result: "failure" });
            trackTelemetryError(caught, "handled_operation");
            setError(t("Serwer logowania jest niedostępny"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            aria-label={t(formLabel)}
            className="student-login-form"
            onSubmit={(event) => void submit(event)}
        >
            <label>
                <span>{t(codeLabel)}</span>
                <input
                    aria-label={t(codeLabel)}
                    autoComplete="one-time-code"
                    autoCapitalize="none"
                    disabled={disabled || submitting}
                    inputMode="text"
                    maxLength={7}
                    onChange={(event) => setCode(formatStudentCode(event.target.value))}
                    pattern="[a-z]{3}-[a-z]{3}"
                    placeholder="fat-tae"
                    required
                    spellCheck={false}
                    value={code}
                />
            </label>
            {error && <p className="form-error" role="alert">{error}</p>}
            <button disabled={disabled || submitting || code.length !== 7} type="submit">
                {submitting ? t("Logowanie…") : t("Zaloguj")}
            </button>
        </form>
    );
};
